import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../styles/AdminUsersPage.css";
import { API_BASE } from "../config";
import { getSession } from "../session";
import apiFetch from "../apiFetch";

const GAME_LABELS = {
  comprehension:       "Comprehension",
  mcq:                 "MCQ Master",
  ranked:              "Ranked",
  book_comprehension:  "Book Comprehension",
  vocab_definitions:   "Vocab: Definitions",
  vocab_synonyms:      "Vocab: Synonyms",
  vocab_antonyms:      "Vocab: Antonyms",
  vocab_mixed:         "Vocab: Mixed",
  synonyms_game:       "Synonyms Game",
  antonyms_game:       "Antonyms Game",
  vocab_matching:      "Vocab Matching",
};

function gameLabel(type) {
  return GAME_LABELS[type] || type;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function SummaryCard({ label, value, color }) {
  return (
    <div className={`rpt-card rpt-card--${color}`}>
      <p className="rpt-card-value">{value ?? "—"}</p>
      <p className="rpt-card-label">{label}</p>
    </div>
  );
}

export default function AdminReportPage() {
  const [summary,   setSummary]   = useState(null);
  const [games,     setGames]     = useState([]);
  const [users,     setUsers]     = useState([]);
  const [status,    setStatus]    = useState("loading");

  const session = getSession();
  const uid = session?.userId ?? "";

  const fetchReport = async () => {
    setStatus("loading");
    try {
      const res = await apiFetch(
        `/api/admin/report?adminUserId=${uid}`,
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 401 || res.status === 403) { setStatus("denied"); return; }
      if (!res.ok) { setStatus("error"); return; }
      const data = await res.json();
      setSummary(data.summary);
      setGames(data.gameBreakdown);
      setUsers(data.userActivity);
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    if (!session) { setStatus("denied"); return; }
    fetchReport();
  }, []);

  if (status === "loading") {
    return <div className="adm-page"><div className="adm-center"><p className="adm-muted">Loading report…</p></div></div>;
  }

  if (status === "denied") {
    return (
      <div className="adm-page">
        <div className="adm-center">
          <div className="adm-denied-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="adm-denied-title">Access Denied</h2>
          <p className="adm-muted">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="adm-page">
        <div className="adm-center">
          <p className="adm-muted">Failed to load report.</p>
          <button className="adm-btn adm-btn-secondary" onClick={fetchReport}>Retry</button>
        </div>
      </div>
    );
  }

  const totalPlays = (summary?.totalPlays ?? 0).toLocaleString();

  return (
    <div className="adm-page">

      {/* ── Header ── */}
      <div className="adm-header">
        <div>
          <h1 className="adm-title">Platform Report</h1>
          <p className="adm-muted">Activity snapshot across all users and games</p>
        </div>
        <div className="adm-header-actions">
          <Link to="/admin/users">
            <button className="adm-btn adm-btn-secondary">← User Management</button>
          </Link>
          <button className="adm-btn adm-btn-primary" onClick={fetchReport}>Refresh</button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="rpt-summary">
        <SummaryCard label="Total users"   value={summary?.totalUsers}  color="purple" />
        <SummaryCard label="Active users"  value={summary?.activeUsers} color="green"  />
        <SummaryCard label="Total plays"   value={totalPlays}           color="blue"   />
        <SummaryCard label="Played today"  value={summary?.todayPlays}  color="yellow" />
      </div>

      {/* ── Game breakdown ── */}
      <section className="rpt-section">
        <h2 className="rpt-section-title">Game breakdown</h2>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Game / Test</th>
                <th>Total plays</th>
                <th>Unique players</th>
                <th>Last played</th>
              </tr>
            </thead>
            <tbody>
              {games.length === 0 && (
                <tr><td colSpan={4} className="adm-empty">No game data yet.</td></tr>
              )}
              {games.map((g) => (
                <tr key={g.type}>
                  <td><strong>{gameLabel(g.type)}</strong></td>
                  <td>{Number(g.plays).toLocaleString()}</td>
                  <td>{Number(g.unique_players).toLocaleString()}</td>
                  <td className="adm-muted-cell">{fmtDateTime(g.last_played)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Per-user activity ── */}
      <section className="rpt-section">
        <h2 className="rpt-section-title">User activity</h2>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Game plays</th>
                <th>Vocab tests</th>
                <th>ELO games</th>
                <th>Total</th>
                <th>Last active</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={8} className="adm-empty">No users found.</td></tr>
              )}
              {users.map((u) => {
                const total = Number(u.game_plays) + Number(u.vocab_plays) + Number(u.elo_plays);
                return (
                  <tr key={u.id} className={!u.active ? "adm-row-inactive" : ""}>
                    <td>
                      <Link to={`/admin/users/${u.id}/report`} className="adm-user-link">
                        <strong>{u.username}</strong>
                      </Link>
                    </td>
                    <td className="adm-muted-cell">{u.email || "—"}</td>
                    <td>
                      <span className={`adm-badge ${u.active ? "adm-badge-active" : "adm-badge-inactive"}`}>
                        {u.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td>{Number(u.game_plays).toLocaleString()}</td>
                    <td>{Number(u.vocab_plays).toLocaleString()}</td>
                    <td>{Number(u.elo_plays).toLocaleString()}</td>
                    <td><strong>{total.toLocaleString()}</strong></td>
                    <td className="adm-muted-cell">{fmtDate(u.last_active)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}
